import {
	Stack,
	Text,
	Button,
	Flex,
	Card,
	Box,
	Group,
	Select,
	Anchor
} from "@mantine/core"
import {
	IconCircleCheckFilled,
	IconExclamationCircleFilled
} from "@tabler/icons-react"
import {
	useSecuritySectionStore,
	SECURITY_FLOWS,
	type MfaMethod
} from "../stores/securitySectionStore"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import type { AEMDictionary } from "@AEM/models/AEMResponse.model"
import { useSecurityMutations } from "../hooks/useSecurityMutations"
import { differenceInWeeks, parseISO, isValid } from "date-fns"
import { MFA_METHODS } from "@utils/constants.util"
import { useIsMobile } from "@utils/device.utils"
import { eventTrackingItems } from "@utils/eventTracking"
import { useUserStore } from "@hooks/useUserStore"
import styles from "./SecurityOverview.module.css"

interface SecurityOverviewProps {
	isFullyCompliant?: boolean
}

const SecurityOverview = ({
	isFullyCompliant = false
}: SecurityOverviewProps) => {
	const { user, setCurrentFlow, updateUser, setCurrentStep, token } =
		useSecuritySectionStore()
	const extendUserProfile = useUserStore((state) => state.extendUserProfile)
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const { updateSecurity } = useSecurityMutations()
	const isMobile = useIsMobile()

	const handleChangeEmail = (validate?: boolean) => {
		setCurrentFlow(SECURITY_FLOWS.EMAIL_CHANGE)
		setCurrentStep({ step: "email-change", data: { validate } })
	}

	const handleChangePassword = () => {
		setCurrentFlow(SECURITY_FLOWS.PASSWORD_CHANGE)
	}

	const handleSetupMFA = (method: MfaMethod) => {
		setCurrentFlow(SECURITY_FLOWS.MFA_SETUP)
		setCurrentStep({ step: "setup", data: { method } })
	}

	const handleConvertUsername = () => {
		setCurrentFlow(SECURITY_FLOWS.USERNAME_TO_EMAIL)
	}

	const emailVerified = Boolean(user?.emailVerified)
	const phoneVerified = Boolean(user?.phoneVerified)
	const emailAddress = user?.email

	const cardProps = {
		p: isMobile ? 20 : 40,
		radius: "sm" as const,
		shadow: "sm" as const,
		bg: "var(--background-color-card)"
	}

	const getMfaTopMessage = (
		emailVerified: boolean,
		phoneVerified: boolean,
		dictionary?: AEMDictionary
	) => {
		if (!emailVerified && !phoneVerified) {
			return dictionary?.hsMfaVerificationMsg ?? ""
		}
		if (emailVerified && !phoneVerified) {
			return dictionary?.multiFactorAuthenticationLinkDescription ?? ""
		}
		if (phoneVerified && !emailVerified) {
			return dictionary?.mfaCodesViaSMSLabel ?? ""
		}
		return dictionary?.multiFactorAuthenticationLinkDescription ?? ""
	}

	const mfaTopMessage = getMfaTopMessage(
		emailVerified,
		phoneVerified,
		dictionary
	)

	const emailStatusText = emailVerified
		? dictionary?.yourEmailHasBeenVerifiedLabel
		: (dictionary?.emailHasNotBeenVerifiedLabel ?? "")
	const emailStatusIcon = emailVerified ? (
		<IconCircleCheckFilled size={24} color="var(--mantine-color-royal-41)" />
	) : (
		<IconExclamationCircleFilled size={24} color="var(--mantine-color-red-6)" />
	)
	const phoneStatusText = phoneVerified
		? dictionary?.mobileNumberSuccessfullyUpdatedPopUpMessage
		: ""
	const phoneStatusIcon = phoneVerified ? (
		<IconCircleCheckFilled size={24} color="var(--mantine-color-royal-41)" />
	) : (
		<IconExclamationCircleFilled
			size={24}
			color="var(--mantine-color-royal-41)"
		/>
	)

	const mfaSettingsButtonLabel = phoneVerified
		? (dictionary?.mfaSettingsButtonLabel ?? "")
		: (dictionary?.addMFAViaSMSLabel ?? "")
	const mfaVerifyEmailLabel = phoneVerified
		? (dictionary?.verifyEmailAddressLabel ?? "")
		: (dictionary?.verifyEmailToMFA ?? "")
	const showVerifyEmailButton = !emailVerified

	const showEmailVerifyButton = !emailVerified
	const showLegacyEmailNote = Boolean(user?.emailIsUsername && emailVerified)

	const getWeeksSincePasswordUpdate = (
		lastUpdateDate?: string
	): string | null => {
		if (!lastUpdateDate) return null

		const parsedDate = parseISO(lastUpdateDate)
		if (!isValid(parsedDate)) return null

		const weeks = differenceInWeeks(new Date(), parsedDate)
		return weeks.toString()
	}

	const lastPasswordUpdatedText = getWeeksSincePasswordUpdate(
		user?.lastPasswordUpdate
	)
	const passwordLastUpdateLabel = lastPasswordUpdatedText
		? (dictionary?.passwordLastUpdateLabel ?? "").replace(
				"()",
				lastPasswordUpdatedText
			)
		: ""

	// Validation to show password description box only if lastPasswordUpdate exists and is before the cutoff date
	const shouldShowPasswordDescription = (): boolean => {
		if (!user?.lastPasswordUpdate) return false

		const lastUpdateDate = parseISO(user.lastPasswordUpdate)
		if (!isValid(lastUpdateDate)) return false

		const cutoffDate = parseISO("2025-09-14")
		return lastUpdateDate <= cutoffDate
	}

	const mfaOptions = [
		{ value: MFA_METHODS.EMAIL, label: dictionary?.mfaDropDownValue3 ?? "" },
		{ value: MFA_METHODS.SMS, label: dictionary?.mfaDropDownValue1 ?? "" }
	]

	const currentPrimaryMfa = (user?.primaryMFA ?? MFA_METHODS.EMAIL) as MfaMethod
	const currentBackupMfa = (user?.secondaryMFA as MfaMethod) ?? null

	const emailSectionCompliant = emailVerified
	const usernameSectionCompliant = Boolean(user?.emailIsUsername)
	const passwordSectionCompliant =
		Boolean(user?.lastPasswordUpdate) && !shouldShowPasswordDescription()
	const primaryMfaSet = Boolean(
		user?.primaryMFA &&
			["EMAIL", "SMS"].includes(String(user.primaryMFA).trim().toUpperCase())
	)
	const mfaSectionCompliant = primaryMfaSet

	const onHandleChangeEvent = (label?: string, type?: string) => {
		eventTrackingItems({
			event_name: "click",
			link_url: "",
			category: "click",
			interactionType: type ?? "",
			navLinkText: label ?? ""
		})
	}

	const getSectionClassName = (compliant: boolean) =>
		isFullyCompliant
			? undefined
			: compliant
				? styles.sectionCompliant
				: styles.sectionNonCompliant

	return (
		<Stack gap={40} maw={{ base: "100%", md: 920 }}>
			<Card
				{...cardProps}
				className={getSectionClassName(usernameSectionCompliant)}
			>
				<Stack gap="md" align="flex-start">
					<Text fw={600} size="lg" component="h2">
						{dictionary?.userNameLabel ?? ""}
					</Text>
					{usernameSectionCompliant ? (
						<Text size="sm">
							{dictionary?.emailAddressIsYourUsernameLabel ?? ""}
						</Text>
					) : (
						<>
							<Text fw={500} size="lg">
								{user?.username}
							</Text>
							<Text size="sm" c="red">
								{dictionary?.hsUserNameUpdate ?? ""}
							</Text>
							<Button
								variant="dark-blue"
								size="lg"
								mt="md"
								onClick={handleConvertUsername}
								w={{ base: "100%", sm: "auto" }}
								maw={{ base: "100%", sm: "none" }}
								styles={{
									label: {
										whiteSpace: "normal",
										wordBreak: "break-word",
										overflowWrap: "break-word"
									}
								}}
							>
								{dictionary?.hsMakeMyEmailMyUsernameButtonLabel ?? ""}
							</Button>
						</>
					)}
				</Stack>
			</Card>

			<Card
				{...cardProps}
				className={getSectionClassName(passwordSectionCompliant)}
			>
				<Flex
					justify="space-between"
					mb="xs"
					direction={{ base: "column", xs: "row" }}
					align={{ base: "flex-start", xs: "center" }}
					gap="xs"
				>
					<Text fw={600} size="lg" component="h2">
						{dictionary?.passwordLabel ?? ""}
					</Text>
					<Anchor
						underline="always"
						onClick={() => {
							handleChangePassword()
							onHandleChangeEvent(
								dictionary?.updatePasswordLabel ?? "",
								"Update Password Click"
							)
						}}
						fw={600}
						tt="capitalize"
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault()
								handleChangePassword()
							}
						}}
						style={{ cursor: "pointer" }}
					>
						{dictionary?.updatePasswordLabel ?? ""}
					</Anchor>
				</Flex>

				<Stack gap="md" align="flex-start">
					<Text size="lg" c={!passwordSectionCompliant ? "red" : undefined}>
						{passwordLastUpdateLabel}
					</Text>

					{shouldShowPasswordDescription() && (
						<Box>
							<Text size="sm" c={!passwordSectionCompliant ? "red" : undefined}>
								{dictionary?.hsUpdatePasswordMsg ?? ""}
							</Text>
						</Box>
					)}
				</Stack>
			</Card>

			<Card
				{...cardProps}
				className={getSectionClassName(emailSectionCompliant)}
			>
				<Flex
					justify="space-between"
					mb="xs"
					direction={{ base: "column", xs: "row" }}
					align={{ base: "flex-start", xs: "center" }}
					gap="xs"
				>
					<Text fw={600} size="lg" component="h2">
						{dictionary?.emailLabel ?? ""}
					</Text>
					<Anchor
						underline="always"
						fw={600}
						onClick={() => {
							handleChangeEmail(false)
							onHandleChangeEvent(dictionary?.updateEmailButtonLabel ?? "")
						}}
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault()
								handleChangeEmail(false)
							}
						}}
						style={{ cursor: "pointer" }}
					>
						{dictionary?.updateEmailButtonLabel ?? ""}
					</Anchor>
				</Flex>

				<Stack gap="md" align="flex-start">
					<Flex
						gap={{ base: "xs", sm: "lg" }}
						mb="xs"
						direction={{ base: "column", sm: "row" }}
						align={{ base: "flex-start", sm: "center" }}
					>
						<Text
							size="lg"
							fw={500}
							style={{
								wordBreak: "break-word",
								overflowWrap: "break-word"
							}}
						>
							{emailAddress}
						</Text>
						<Flex align="center" gap="xs">
							{emailStatusIcon}
							<Text
								size="xs"
								c={!emailSectionCompliant ? "red" : undefined}
								style={{
									wordBreak: "break-word",
									overflowWrap: "break-word",
									...(emailSectionCompliant
										? { color: "var(--mantine-color-royal-41)" }
										: {})
								}}
							>
								{emailStatusText}
							</Text>
						</Flex>
					</Flex>

					{showEmailVerifyButton && (
						<>
							<Text size="sm" c={!emailSectionCompliant ? "red" : undefined}>
								{dictionary?.hsEmailVerificationOfMfa ?? ""}
							</Text>

							<Anchor
								underline="always"
								fw={600}
								onClick={() => handleChangeEmail(true)}
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault()
										handleChangeEmail(true)
									}
								}}
								style={{ cursor: "pointer" }}
								component="button"
							>
								{dictionary?.verifyEmailAddressLabel ?? ""}
							</Anchor>
						</>
					)}

					{showLegacyEmailNote && (
						<Text size="sm">{dictionary?.emailAddressIsYourUsernameLabel}</Text>
					)}
				</Stack>
			</Card>

			<Card {...cardProps} className={getSectionClassName(mfaSectionCompliant)}>
				<Stack gap="md" align="flex-start">
					<Text fw={600} size="lg" component="h2">
						{dictionary?.multiFactorAuthenticationLabel ?? ""}
					</Text>

					<Text size="sm" c={!mfaSectionCompliant ? "red" : undefined}>
						{mfaTopMessage}
					</Text>
					<Stack gap={0} align="flex-start">
						<Flex
							gap={{ base: "xs", sm: "lg" }}
							direction={{ base: "column", sm: "row" }}
							align={{ base: "flex-start", sm: "center" }}
						>
							<Text
								style={{
									wordBreak: "break-word",
									overflowWrap: "break-word"
								}}
							>
								{emailAddress}
							</Text>
							<Flex align="center" gap="xs">
								{emailStatusIcon}
								<Text
									size="xs"
									c={!mfaSectionCompliant ? "red" : undefined}
									style={{
										wordBreak: "break-word",
										overflowWrap: "break-word",
										...(mfaSectionCompliant
											? { color: "var(--mantine-color-royal-41)" }
											: {})
									}}
								>
									{emailStatusText}
								</Text>
							</Flex>
						</Flex>
						{emailVerified && (
							<Anchor
								underline="always"
								fw={600}
								onClick={() => handleChangeEmail(false)}
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault()
										handleChangeEmail(false)
									}
								}}
								style={{ cursor: "pointer" }}
							>
								{dictionary?.changeEmailLabel}
							</Anchor>
						)}
					</Stack>

					{phoneVerified && (
						<Stack gap={0} align="flex-start">
							<Group align="center" gap="xs">
								<Text>{user?.phone}</Text>
								{phoneStatusIcon}
								<Text size="xs">{phoneStatusText}</Text>
							</Group>

							<Anchor
								underline="always"
								onClick={() => handleSetupMFA("SMS")}
								fw={600}
							>
								{dictionary?.changeMobileLabel}
							</Anchor>
						</Stack>
					)}

					<Stack gap="xs" align="flex-start">
						{showVerifyEmailButton && (
							<Anchor
								onClick={() => handleChangeEmail(true)}
								underline="always"
								fw={600}
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault()
										handleChangeEmail(true)
									}
								}}
								style={{ cursor: "pointer" }}
								component="button"
							>
								{mfaVerifyEmailLabel}
							</Anchor>
						)}
						<Anchor
							onClick={() => handleSetupMFA("SMS")}
							underline="always"
							fw={600}
							tabIndex={0}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault()
									handleSetupMFA("SMS")
								}
							}}
							style={{ cursor: "pointer" }}
						>
							{mfaSettingsButtonLabel}
						</Anchor>
					</Stack>
				</Stack>
			</Card>
			{/* MFA selection Section */}
			{emailVerified && phoneVerified && (
				<Card {...cardProps}>
					{dictionary?.chooseMFAText}
					<Flex
						direction={{ base: "column", md: "row" }}
						align="space-between"
						justify="center"
						gap="xl"
					>
						<Select
							label={dictionary?.primaryMFALabel}
							data={mfaOptions}
							value={currentPrimaryMfa}
							onChange={(val) => {
								const selected = val as MfaMethod
								const backupConflicts = currentBackupMfa === selected
								const newBackup = backupConflicts ? null : currentBackupMfa
								const payload = {
									token: token ?? "",
									primaryMFA: selected,
									...(newBackup ? { secondaryMFA: newBackup } : {})
								}
								updateUser({
									primaryMFA: selected,
									...(backupConflicts ? { secondaryMFA: null } : {})
								})
								extendUserProfile({
									primaryMFA: selected.toLowerCase(),
									...(backupConflicts ? { secondaryMFA: "" } : {})
								})
								updateSecurity.mutate(payload)
							}}
							checkIconPosition="right"
							allowDeselect={false}
							w="100%"
							flex={1}
						/>
						<Select
							label={dictionary?.backupMFALabel}
							data={mfaOptions.filter((o) => o.value !== currentPrimaryMfa)}
							value={currentBackupMfa}
							placeholder={dictionary?.backupMFALabel}
							onChange={(val) => {
								const selected = val as MfaMethod
								const payload = {
									token: token ?? "",
									secondaryMFA: selected,
									primaryMFA: currentPrimaryMfa
								}
								updateUser({ secondaryMFA: selected })
								extendUserProfile({
									secondaryMFA: selected.toLowerCase()
								})
								updateSecurity.mutate(payload)
							}}
							checkIconPosition="right"
							allowDeselect={false}
							w="100%"
							flex={1}
						/>
					</Flex>
				</Card>
			)}
		</Stack>
	)
}

export default SecurityOverview
